import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { logoutUser } from '../auth/authSlice';

const baseUrl = process.env.REACT_APP_REACT_APP_BACKEND_BASE_URL;
const baseQuery = fetchBaseQuery({baseUrl: baseUrl, credentials: "include"});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions)
  if (result.error && result.error.status === 401) {
    api.dispatch(logoutUser())
    localStorage.setItem('sessionTimeout', 'true');
    window.location.href = '/login/'
  }
  return result
}

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Client'],
  endpoints: builder => ({
    getClient: builder.query({
      query: (searchData) => {
        const params = {
          username: searchData.customerName,
          uid: searchData.customerId,
          status: searchData.customerStatus,
          rate_template: searchData.customerCategory,
          user: searchData.customerAssignedTo,
          business_type: searchData.businessType,
          page_size: searchData.pageSize,
          curr_page: searchData.current,
        }
        return {
          url: 'accounts/get-customer/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['Client'],
    }),
    createClient: builder.mutation({
      query: (data) => ({
        url: 'accounts/create-customer/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['PendingClient', 'Client', 'CustomerUidSearchSelect'],
    }),
    createPendingClient: builder.mutation({
      query: (data) => ({
        url: 'accounts/create-pending-customer/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['PendingClient'],
    }),
    rejectTheReview: builder.mutation({
      query: (data) => ({
        url: `accounts/reject-the-review/${data.id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['PendingClient'],
    }),
    createUserManagement: builder.mutation({
      query: (data) => ({
        url: 'accounts/create-user-management/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['UserManagement'],
    }),
    createRole: builder.mutation({
      query: (data) => ({
        url: 'accounts/create-role/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['RoleManagement'],
    }),
    createDepartment: builder.mutation({
      query: (data) => ({
        url: 'accounts/create-department/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['DepartmentManagement'],
    }),
    createLocation: builder.mutation({
      query: (data) => ({
        url: 'accounts/create-location/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['locations'],
    }),
    updateClient: builder.mutation({
      query: (data) => ({
        url: `accounts/update-customer/${data.id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Client'],
    }),
    updateUserManagement: builder.mutation({
      query: (data) => ({
        url: `accounts/update-user-management/${data.id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['UserManagement', 'CustomerUidSearchSelect'],
    }),
    updateLocation: builder.mutation({
      query: (data) => ({
        url: `accounts/update-location/${data.id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['locations', 'CustomerUidSearchSelect'],
    }),
    updateDepartment: builder.mutation({
      query: (data) => ({
        url: `accounts/update-department/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['DepartmentManagement', 'CustomerUidSearchSelect', "UpdateDepartmentDataPermission"],
    }),
    updateRole: builder.mutation({
      query: (data) => ({
        url: `accounts/update-role/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['RoleManagement', 'CustomerUidSearchSelect'],
    }),
    updateClientMWT: builder.mutation({
      query: (data) => ({
        url: `accounts/update-customer-mwt/${data.id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Client', 'CustomerUidSearchSelect'],
    }),
    bulkUpdateClientStatus: builder.mutation({
      query: (data) => ({
        url: `accounts/bulk-update-customer-status/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Client', 'CustomerUidSearchSelect'],
    }),
    updateClientDateInterval: builder.mutation({
      query: (data) => ({
        url: `accounts/update-customer-date-interval/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Client', 'CustomerUidSearchSelect'],
    }),
    deleteClient: builder.mutation({
      query: (data) => ({
        url: `accounts/delete-customer/`,
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: ['Client', 'CustomerUidSearchSelect'],
    }),
    getUser: builder.query({
      query: () => ({
        url: 'accounts/get-user/',
        method: 'GET',
      }),
    }),
    getLocation: builder.query({
      query: (data) => {
      const request = {
        name: data.name,
        page_size: data.pageSize,
        curr_page: data.current,
      }

      return {
        url: 'accounts/get-location/',
        method: 'GET',
        params: request,
      }},
      providesTags: ['locations'],
    }),
    getRoles: builder.query({
      query: (searchData) => {
        const params = {
          role: searchData.name,
          department: searchData.department,
          status: searchData.status,
          page_size: searchData.pageSize,
          curr_page: searchData.current,
        }

        return {
          url: 'accounts/get-role/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['RoleManagement'],
    }),
    getDepartments: builder.query({
      query: (searchData) => {
        const params = {
          location: searchData.location,
          page_size: searchData.pageSize,
          curr_page: searchData.current,
        }

        return {
          url: 'accounts/get-department/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['DepartmentManagement'],
    }),
    getPendingCustomer: builder.query({
      query: (searchData) => {
        return {
          url: 'accounts/get-pending-customers/',
          method: 'GET',
          params: searchData,
        }
      },
      providesTags: ['PendingClient'],
    }),
    getRateTemplate: builder.query({
      query: () => ({
        url: 'accounts/get-rateTemplate/',
        method: 'GET',
      }),
      providesTags: ['RateTemplate'],
    }),
    getLogisticsSupplier: builder.query({
      query: () => ({
        url: 'accounts/get-all-logistics-supplier/',
        method: 'GET',
      }),
      providesTags: ['LogisticsSupplier'],
    }),
    getLogisticsService: builder.query({
      query: (searchData) => {
        return Object.keys(searchData).length > 0
      ? {
          url: 'accounts/get-logistics-services/',
          method: 'GET',
          params: {
            logistics_supplier: searchData.logisticsSupplier,
            page_size: searchData.pageSize,
            curr_page: searchData.currentPage,
          },
        }
      : {
          url: 'accounts/get-logistics-services/',
          method: 'GET',
          params: {
            page_size: searchData.pageSize,
            curr_page: searchData.currentPage,
          },
        };
      }
      ,providesTags: ['LogisticsService'],
    }),
    createLogisticsService: builder.mutation({
      query: (data) => ({
        url: 'accounts/create-logistics-service/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['LogisticsService', 'LogisticsSupplier'],
    }),
    updateLogisticsService: builder.mutation({
      query: (data) => ({
        url: `accounts/update-logistics-service/${data.id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['LogisticsService'],
    }),
    getQuoteSheet: builder.mutation({
      query: (data) => ({
        url: 'accounts/get-quote-sheet/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['QuoteSheet'],
    }),
    getRateExcel: builder.query({
      query: (searchData) => {
        let params = {
          logistics_service: searchData.logisticsService,
          logistics_supplier: searchData.logisticsSupplier,
          rate_template: searchData.rateTemplate,
          effective_date: searchData.effectiveDate?.substring(0,10),
          user: searchData.user,
          page_size: searchData.pageSize,
          curr_page: searchData.current,
        }
        return {
          url: 'accounts/get-rate-excel/',
          method: 'GET',
          params: params,
        };
      },
      providesTags: ['RateExcel'],
    }),
    getAllCustomerUid: builder.query({
      query: (data) => ({
        url: 'accounts/get-all-customer-uid/',
        method: 'GET',
      }),
      providesTags: ['CustomerUidSearchSelect']
    }),
    getLogisticsServiceByName: builder.query({
      query: (data) => ({
        url: 'accounts/get-all-logistics-service-by-name/',
        method: 'GET',
      }),
      providesTags: ['logisticsService']
    }),
    verifyQuoteSheet: builder.mutation({
      query: (data) => ({
        url: 'accounts/verify-quote-sheet/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['RateExcel'],
    }),
    getBillingInvoice: builder.query({
      query: (data) => {
        const params = {
          page_size: data.pageSize,
          curr_page: data.current,
          source_type: data.source_type,
        }
        return {
          url: 'accounts/get-billing-invoices/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['BillingInvoice'],
    }),
    uploadBillingInvoice: builder.mutation({
      query: (data) => ({
        url: 'accounts/upload-billing-invoice/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['BillingInvoice', 'BillingProcess', 'BillingDetails', 'PayableInvoices', 'BillingInvoiceSearchSelect'],
    }),
    deleteBillingInvoice: builder.mutation({
      query: (data) => ({
        url: `accounts/delete-billing-invoice/`,
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: ['BillingInvoice', 'BillingProcess', 'BillingDetails', 'BillingInvoiceSearchSelect'],
    }),
    getBillingProcess: builder.query({
      query: (searchData) => {
        let params = {
          billing_invoice: searchData.billingInvoice,
          user: searchData.user,
          customer: searchData.customer,
          status: searchData.status,
          page_size: searchData.pageSize,
          curr_page: searchData.current,
        }
        if (searchData?.created_at) {
          params.created_at_after = searchData.created_at[0].substring(0, 10);
          params.created_at_before = searchData.created_at[1].substring(0, 10);
        }
        return {
          url: 'accounts/get-billing-process/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['BillingProcess'],
    }),
    getBillingInvoiceNames: builder.query({
      query: (data) => ({
        url: 'accounts/get-billing-invoices-names/',
        method: 'GET',
      }),
      providesTags: ['BillingInvoiceSearchSelect']
    }),
    getBillingDetails: builder.query({
      query: (searchData) => {
        const params = {
          page_size: searchData.pageSize,
          curr_page: searchData.currentPage,
          status: searchData.status,
          bill_to_account_number: searchData.billToAccountNumber,
          invoice_date_before: searchData.invoiceDateBefore,
          invoice_date_after: searchData.invoiceDateAfter,
          invoice_number: searchData.invoiceNumber,
          logistics_service: searchData.logisticsService,
          uid: searchData.uid,
          billing_invoice: searchData.billingInvoice,
          sort_field: searchData.sortField,
          sort_order: searchData.sortOrder,
          shipment_date_before: searchData.shipmentDateBefore,
          shipment_date_after: searchData.shipmentDateAfter,
          id_to_download: searchData.idToDownload,
          isDownload: searchData.isDownload,
          dispute_status: searchData.disputeStatus,
        };
        const bodyData = new FormData();
        if (searchData.trackingNumber) {
          bodyData.append('tracking_number', searchData.trackingNumber);
        }
        if (searchData.tdMasterTracking) {
          bodyData.append('td_master_tracking', searchData.tdMasterTracking);
        }

        return {
          url: 'accounts/get-billing-details/',
          method: 'POST',
          params: params,
          body: bodyData,
          formData: true,
        };
      },
      providesTags: ['BillingDetails'],
    }),
    getBillingDetailsWithError: builder.query({
      query: (searchData) => {
        const params = {
          billing_invoice: searchData.billingInvoice,
          tracking_number: searchData.trackingNumber,
          uid: searchData.uid,
          status: searchData.currentTab,
          dup_status: searchData.dup_status,
          service_match: searchData.service_match,
          logistics_service: searchData.logisticsService,
          master_mismatch_status: searchData.masterMismatchStatus,
          user_defined_exceptions: searchData.userDefinedExceptions,
          unrecognized_surcharges: searchData.unrecognizedSurcharges,
          page_size: searchData.pageSize,
          curr_page: searchData.currentPage,
        }
        return {
          url: 'accounts/get-billing-details-with-error/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['BillingDetailsWithError'],
    }),
    updateUid: builder.mutation({
      query: (data) => ({
        url: 'accounts/update-uid-with-billing-details/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['BillingInvoice', 'BillingDetailsWithError', 'BillingDetails', 'BillingProcess', 'BillingInvoice'],
    }),
    batchUpdateUid: builder.mutation({
      query: (data) => ({
        url: 'accounts/batch-update-uid-with-billing-details/',
        method: 'POST',
        body: data,
        headers: {
          'Content-Type': 'application/json' // 明确指定发送内容为 JSON
        }
        // formData: true,
      }),
      invalidatesTags: ['BillingInvoice', 'BillingDetailsWithError', 'BillingDetails', 'BillingProcess', 'BillingInvoice'],
    }),
    getAsyncManagement: builder.query({
      query: (searchData) => {
        const params = {
          record_type: searchData.record_type,
          user: searchData.user,
          status: searchData.status,
          page_size: searchData.pageSize,
          curr_page: searchData.current,
        }
        if (searchData?.created_at) {
          params.created_at_after = searchData.created_at[0].substring(0,10);
          params.created_at_before = searchData.created_at[1].substring(0,10);
        }
        return {
          url: 'accounts/get-async-management/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['AsyncManagement'],
    }),

    getUserManagement: builder.query({

      query: (searchData) => {
        const params = {
          username: searchData.username,
          email: searchData.email,
          role: searchData.role,
          department: searchData.department_name,
          status: searchData.status,
          page_size: searchData.pageSize,
          curr_page: searchData.current,
        }
        if (searchData?.created_at) {
          params.created_at_after = searchData.created_at[0].substring(0,10);
          params.created_at_before = searchData.created_at[1].substring(0,10);
        }
        return {
          url: 'accounts/get-user-management/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['UserManagement'],
    }),
    deleteAsyncManagement: builder.mutation({
      query: (data) => ({
        url: `accounts/delete-async-management/`,
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: ['AsyncManagement'],
    }),
    exportUidFiles: builder.mutation({
      query: (data) => ({
        url: `/accounts/export-uid-files/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['AsyncManagement'],
    }),
    getRecordType: builder.query({
      query: () => ({
        url: 'accounts/get-record-type/',
        method: 'GET',
      }),
      providesTags: ['RecordType'],
    }),
    disputeBillingDetails: builder.mutation({
      query: (data) => ({
        url: 'accounts/dispute-billing-details/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['BillingDetails', 'BillingDetailsWithError', 'BillingProcess'],
    }),
    statusUpdateHandler: builder.mutation({
      query: (data) => ({
        url: 'accounts/billing-details-status-update/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['BillingDetails', 'BillingDetailsWithError', 'BillingProcess'],
    }),
    statusUpdateHandlerV2: builder.mutation({
      query: (data) => ({
        url: 'accounts/billing-details-status-update-v2/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['BillingDetails', 'BillingDetailsWithError', 'BillingProcess'],
    }),
    GenerateBillingBatchDetails: builder.mutation({
      query: (data) => {
        const body = {
          billing_batch_id: data.billing_batch_id
        }
        if (data.ids){
          body['ids'] = data.ids;
        }
        return {
          url: 'accounts/generate-billing-batch-billing-details/',
          method: 'POST',
          params: {
            status: data.status,
            bill_to_account_number: data.billToAccountNumber,
            invoice_date_before: data.invoiceDateBefore,
            invoice_date_after: data.invoiceDateAfter,
            invoice_number: data.invoiceNumber,
            logistics_service: data.logisticsService,
            td_master_tracking: data.tdMasterTracking,
            tracking_number: data.trackingNumber,
            uid: data.uid,
            billing_invoice: data.billingInvoice,
            shipment_date: data.shipmentDate,
          },
          body: body,
        }
      },
      invalidatesTags: ['BillingDetails', 'Invoice', 'HSPCalculationDetails', 'GroundCalculationDetails'],
    }),
    GenerateBillingBatchShipments: builder.mutation({
      query: (data) => {
        const body = {
          billing_batch_id: data.billing_batch_id
        }
        if (data.ids) {
          body['ids'] = data.ids;
        }
        return {
          url: 'accounts/generate-billing-batch-shipments/',
          method: 'POST',
          params: {
            status: data.status,
            master_tracking_number: data.masterTrackingNumber,
            uid: data.uid,
            shipment_date: data.shipmentDate,
          },
          body: body,
        }
      },
      invalidatesTags: ['BillingDetails', 'Invoice', 'HSPCalculationDetails', 'GroundCalculationDetails'],
    }),
    SunkCost: builder.mutation({
      query: (data) => ({
        url: 'accounts/sunk-cost-billing-details/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['BillingDetails', 'BillingDetailsWithError', 'BillingProcess'],
    }),
    getFuelSurchargeManagement: builder.query({
      query: (searchData) => {
        let params = {
          logistics_service: searchData.logisticsService,
          curr_page: searchData.current,
          page_size: searchData.pageSize,
        }
        const field = searchData.sorter?.field
        const order = searchData.sorter?.order === 'descend' ? `-${field}` : field;
        if (field) {
          params.ordering = order;
        }
        if (searchData?.eff_time) {
          params.eff_time_after = searchData.eff_time[0].substring(0,10);
          params.eff_time_before = searchData.eff_time[1].substring(0,10);
        }
        return {
            url: 'accounts/get-fuel-surcharge-management/',
            method: 'GET',
            params: params,
        }
      },
      providesTags: ['FuelSurchargeManagement'],
    }),
    updateFuelSurchargeManagement: builder.mutation({
      query: (data) => ({
        url: 'accounts/update-fuel-surcharge-management/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['FuelSurchargeManagement'],
    }),
    getNonStandardService: builder.query({
      query: (searchData) => {
        return Object.keys(searchData).length > 0
      ? {
          url: 'accounts/get-non-standard-service/',
          method: 'GET',
          params: {
            bill_to_account_number: searchData.billToAccountNumber,
            invoice_date_before: searchData.invoiceDateBefore,
            invoice_date_after: searchData.invoiceDateAfter,
            invoice_number: searchData.invoiceNumber,
            logistics_service: searchData.logisticsService,
            tracking_number: searchData.trackingNumber,
            uid: searchData.uid,
            page_size: searchData.pageSize,
            curr_page: searchData.currentPage,
            billing_invoice: searchData.billingInvoice,
            status: searchData.status,
          },
        }
      : {
          url: 'accounts/get-non-standard-service/',
          method: 'GET',
          params: {
            page_size: searchData.pageSize,
            curr_page: searchData.currentPage,
          },
        };
      },
      providesTags: ['NonStandardServiceBillingDetails'],
    }),
    getInvoice: builder.query({
      query: (searchData) => {

        let params = {
          uid: searchData.uid,
            billing_invoice: searchData.billingInvoice,
            status: searchData.status,
            logistics_service: searchData.logisticsService,
            billing_batch_id: searchData.billingBatchId,
            page_size: searchData.pageSize,
            curr_page: searchData.current,
        }

        if (searchData?.bill_time) {
          params.bill_time_after = searchData.bill_time[0].substring(0,10);
          params.bill_time_before = searchData.bill_time[1].substring(0,10);
        }

        return {
          url: 'accounts/get-invoice/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['Invoice'],
    }),
    downloadBillingBatch: builder.mutation({
      query: (data) => ({
        url: 'accounts/download-billing-batch/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['Invoice', 'AsyncManagement'],
    }),
    updateMasterTracking: builder.mutation({
      query: (data) => ({
        url: 'accounts/update-master-tracking/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['BillingDetailsWithError', 'BillingDetails', 'BillingProcess', 'BillingInvoice'],
    }),
    getTrackingMasterTracking: builder.mutation({
      query: (data) => ({
        url: 'accounts/get-tracking-master-tracking/',
        method: 'POST',
        body: data,
      }),
    }),
    modifyService: builder.mutation({
      query: (data) => ({
        url: 'accounts/modify-service/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['BillingDetailsWithError']
    }),
    getNonOriginalBillingDetails: builder.query({
      query: (searchData) => {
        return Object.keys(searchData).length > 0
      ? {
          url: 'accounts/get-non-original-billing-details/',
          method: 'GET',
          params: {
            billing_invoice: searchData.billingInvoice,
            page_size: searchData.pageSize,
            curr_page: searchData.currentPage,
          },
        }
      : {
          url: 'accounts/get-non-original-billing-details/',
          method: 'GET',
          params: {
            page_size: searchData.pageSize,
            curr_page: searchData.currentPage,
          },
        };
      },
      providesTags: ['NonOriginalBillingDetails'],
    }),
    getChargeCode: builder.query({
      query: () => ({
        url: 'accounts/get-charge-code/',
        method: 'GET',
      }),
      providesTags: ['ChargeCode'],
    }),
    getChargeCodeDescriptions: builder.query({
      query: () => ({
        url: 'accounts/get-charge-code-descriptions/',
        method: 'GET',
      }),
      providesTags: ['ChargeCodeDescriptions'],
    }),
    ConvertZZZToCharge: builder.mutation({
      query: (data) => ({
        url: 'accounts/convert-zzz-to-charge/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['BillingDetailsWithError', 'BillingDetails', 'BillingProcess', 'BillingInvoice'],
    }),
    getAWSFileManagement: builder.query({
      query: (searchData) => {
        const field = searchData.sorter?.field
        const order = searchData.sorter?.order === 'descend' ? `-${field}` : field;
        return Object.keys(searchData).length > 0
            ? {
              url: 'accounts/get-aws-file-manager/',
              method: 'GET',
              params: {
                logistics_service: searchData.logisticsService,
                eff_time: searchData.allMondays,
                curr_page: searchData.currentPage,
                page_size: searchData.pageSize,
                ordering: order
              },
            }
            : {
              url: 'accounts/get-aws-file-manager/',
              method: 'GET',
              params: {
                curr_page: searchData.currentPage,
                page_size: searchData.pageSize,
                ordering: order
              },
            }
      },
      providesTags: ['FuelSurchargeManagement'],
    }),
    updateUidWithoutStatusChange: builder.mutation({
      query: (data) => ({
        url: 'accounts/update-uid-without-status-change/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['BillingDetails'],
    }),
    splitUserChange: builder.mutation({
      query: (data) => ({
        url: 'accounts/split-user-change/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['BillingDetails'],
    }),
    ShipmentRateCalculation: builder.mutation({
      query: (data) => ({
        url: 'shipment_rate/shipment-rate-calculation/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['BillingDetails', 'HSPCalculationDetails', 'GroundCalculationDetails'],
    }),
    ReShipmentRateCalculation: builder.mutation({
      query: (data) => ({
        url: 'shipment_rate/re-shipment-rate-calculation/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['BillingDetails', 'Invoice', 'HSPCalculationDetails', 'GroundCalculationDetails'],
    }),
    getGroundCalculationDetails: builder.query({
      query: (searchData) => {
        const params = {
          page_size: searchData.pageSize,
          curr_page: searchData.currentPage,
          master_tracking_number: searchData.masterTrackingNumber,
          uid: searchData.uid,
          shipment_date: searchData.shipmentDate,
          invoice: searchData.invoice,
          status: searchData.status,
          ground_service: searchData.groundServices,
          id_to_download: searchData.idToDownload,
          sort_field: searchData.sortField,
          sort_order: searchData.sortOrder,
        };
        return {
          url: 'accounts/get-ground-calculation_details/',
          method: 'GET',
          params: params,
        };
      },
      providesTags: ['GroundCalculationDetails'],
    }),
    getHSPCalculationDetails: builder.query({
      query: (searchData) => {
        const params = {
          status: searchData.status,
          page_size: searchData.pageSize,
          curr_page: searchData.currentPage,
          logistics_service: searchData.logisticsService,
          td_master_tracking: searchData.tdMasterTracking,
          tracking_number: searchData.trackingNumber,
          billing_invoice: searchData.billingInvoice,
          invoice: searchData.invoice,
          uid: searchData.uid,
          shipment_date: searchData.shipmentDate,
          id_to_download: searchData.idToDownload,
          sort_field: searchData.sortField,
          sort_order: searchData.sortOrder,
        };
        return {
          url: 'accounts/get-home-sp-calculation-details/',
          method: 'GET',
          params: params,
        };
      },
      providesTags: ['HSPCalculationDetails'],
    }),
    getDupDetails: builder.query({
      query: (data) => {
        return {
          url: 'accounts/get-dup-details/',
          method: 'GET',
          params: data,
        }
      }
    }),
    CompulsoryPayment: builder.mutation({
      query: (data) => ({
        url: 'accounts/compulsory-payment/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['BillingDetails', 'BillingDetailsWithError', 'BillingProcess'],
    }),
    TemporaryRetrieveMasterTracking: builder.mutation({
      query: (data) => ({
        url: 'accounts/temporary-update-status/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['BillingDetails', 'BillingDetailsWithError', 'BillingProcess'],
    }),
    GroundShipmentRateCalculation: builder.mutation({
      query: (data) => ({
        url: 'shipment_rate/ground-shipment-rate-calculation/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['BillingDetails', 'HSPCalculationDetails', 'GroundCalculationDetails'],
    }),
    createRateTemplate: builder.mutation({
      query: (data) => ({
        url: 'accounts/create-rate-template/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['RateTemplate'],
    }),
    addNewFuelSurcharge: builder.mutation({
      query: (data) => ({
        url: 'accounts/add-new-fuel-surcharge/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['FuelSurchargeManagement'],
    }),
    GetRuleExpressions: builder.query({
      query: (searchData) => {
        const params = {
          page_size: searchData.pageSize,
          curr_page: searchData.currentPage,
        };
        return {
          url: 'accounts/get-rule-expressions/',
          method: 'GET',
          params: params,
        };
      },
      providesTags: ['RuleExpressions'],
    }),
    createRuleExpression: builder.mutation({
      query: (data) => ({
        url: 'accounts/create-new-rule-expression/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['RuleExpressions'],
    }),
    AddCostSurcharge: builder.mutation({
      query: (data) => ({
        url: 'accounts/cost-add-surcharges/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['CostQuoSheet'],
    }),
    editRuleExpression: builder.mutation({
      query: (data) => ({
        url: 'accounts/edit-rule-expression/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['RuleExpressions'],
    }),
    deleteRuleExpression: builder.mutation({
      query: (data) => ({
        url: 'accounts/delete-rule-expression/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['RuleExpressions'],
    }),
    GetRuleExpressionsNames: builder.query({
      query: () => {
        return {
          url: 'accounts/get-rule-expressions-names/',
          method: 'GET',
        };
      },
    }),
    ReevaluateBillingDetails: builder.mutation({
      query: (data) => {
        return {
          url: 'accounts/reevaluate-billing-details/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['BillingDetails', 'BillingDetailsWithError', 'BillingProcess'],
    }),
    getAllCustomerWithGroup: builder.query({
      query: () => ({
        url: 'accounts/get-all-customer-with-group/',
        method: 'GET',
      }),
    }),
    updateBillingStatus: builder.mutation({
      query: (searchData) => {
        return {
          url: 'accounts/update-billing-status/',
          method: 'POST',
          params: {
            bill_to_account_number: searchData.billToAccountNumber,
            invoice_date_before: searchData.invoiceDateBefore,
            invoice_date_after: searchData.invoiceDateAfter,
            invoice_number: searchData.invoiceNumber,
            logistics_service: searchData.logisticsService,
            tracking_number: searchData.trackingNumber,
            uid: searchData.uid,
            page_size: searchData.pageSize,
            curr_page: searchData.currentPage,
            billing_invoice: searchData.billingInvoice,
            status: searchData.status,
          },
          body: {
            'data': searchData,
            'invoice_name': searchData.invoiceName,
          },
        }
      },
      invalidatesTags: ['NonStandardServiceBillingDetails']
    }),
    labelReuse: builder.mutation({
      query: (data) => ({
        url: 'accounts/label-reuse/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['BillingDetails', 'BillingDetailsWithError', 'BillingProcess'],
    }),
    getBillingDetailsSummary: builder.query({
      query: (data) => {
        let params = {
          uid: data.uid,
          logistics_service: data.logistics_service,
          page_size: data.pageSize,
          curr_page: data.current,
        }
        if (data?.invoice_date) {
          params.invoice_date_after = data.invoice_date[0].substring(0,10);
          params.invoice_date_before = data.invoice_date[1].substring(0,10);
        }
        return {
          url: 'accounts/get-billing-details-summary/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['BillingDetailsSummary'],
    }),
    getInvoiceByName: builder.query({
      query: (data) => {
        return {
          url: 'accounts/get-invoice-by-name/',
          method: 'GET',
        }
      }
    }),
    getRuleExpressionNames: builder.query({
      query: () => {
        return {
          url: 'accounts/get-rule-expression-without-pagination/',
          method: 'GET',
        }
      }
    }),
    getUserByDepartment: builder.query({
      query: (department) => {
        const params = {
          'department': department
        }
        return {
          url: 'accounts/get-user-by-department/',
          method: 'GET',
          params: params,
        }
      }
    }),
    editBillingBatchInfo: builder.mutation({
      query: (data) => {
        const formData = new FormData();
        formData.append('invoice_id', data.invoice_id);
        formData.append('actual_billed_amount', data.actual_billed_amount);
        formData.append('actual_billed_time', data.actual_billed_time);
        return {
          url: 'accounts/edit-billing-batch-info/',
          method: 'POST',
          body: formData,
          formData: true,
        }
      },
      invalidatesTags: ['Invoice']
    }),
    getAccountReceivable: builder.query({
      query: (data) => {
        let params = {
          customer: data.customer,
          billing_cycle: data.billing_cycle,
          contact: data.contact,
          biller: data.biller,
          invoice_number: data.invoice_number,
          business_type: data.business_type,
          remittee: data.remittee,
          customer_name: data.customer_name,
          account_invoice_status: data.account_invoice_status,
          overdue_days: data.overdue_days,
          page_size: data.pageSize,
          curr_page: data.current,
        }
        if (data?.invoice_date) {
          params.invoice_date_after = data.invoice_date[0].substring(0,10);
          params.invoice_date_before = data.invoice_date[1].substring(0,10);
        }
        if (data?.revenue_confirm_date) {
          params.revenue_confirm_date = data.revenue_confirm_date.substring(0,10);

        }
        if (data?.accounting_confirm_date) {
          params.accounting_confirm_date = data.accounting_confirm_date.substring(0,10);
        }
        console.log(data);
        if (data?.sort) {
          let ordering = '';
          for (const [key, value] of Object.entries(data.sort)) {
            ordering += value === 'descend' ? `-${key},` : `${key},`;
          }
          params.ordering = ordering.slice(0, -1);

        }
        return {
          url: 'accounts/get-account-receivable/',
          method: 'POST',
          params: params,
        }
      },
      providesTags: ['AccountReceivable']
    }),
    createAccountReceivable: builder.mutation({
      query: (data) => {
        return {
          url: 'accounts/create-account-receivable/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['AccountReceivable', 'CustomerBalance']
    }),
    deleteAccountReceivable: builder.mutation({
      query: (data) => {
        return {
          url: 'accounts/delete-account-receivable/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['AccountReceivable', 'CustomerBalance'],
    }),
    makePayment: builder.mutation({
      query: (data) => {
        return {
          url: 'accounts/make-payment/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['AccountReceivable']
    }),
    updateAccountReceivable: builder.mutation({
      query: (data) => {
        let request = {};
        if (data?.revenue_confirm_date) {
          request.revenue_confirm_date = data.revenue_confirm_date;
        }
        if (data?.accounting_confirm_date) {
          request.accounting_confirm_date = data.accounting_confirm_date;
        }
        return {
          url: `accounts/update-account-receivable/${data.invoice_id}/`,
          method: 'PATCH',
          body: request,
        }
      },
      invalidatesTags: ['AccountReceivable', 'CustomerBalance']
    }),
    editingAccountReceivable: builder.mutation({
      query: (data) => {
        return {
          url: `accounts/editing-account-receivable/${data.invoice_id}/`,
          method: 'PATCH',
          body: data,
        }
      },
      invalidatesTags: ['AccountReceivable', 'Credits']
    }),
    adjustSurcharges: builder.mutation({
      query: (data) => {
        return {
          url: 'accounts/adjust-surcharges/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['BillingDetails', 'BillingDetailsWithError', 'BillingProcess'],
    }),
    disputeSurcharges: builder.mutation({
      query: (data) => {
        return {
          url: 'accounts/dispute-surcharges/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['BillingDetails', 'BillingDetailsWithError', 'BillingProcess'],
    }),
    getAccountReceivableAndRiskControlOperationLog: builder.query({
      query: (invoice_id) => {
        return {
          url: `accounts/get-account-receivable-and-risk-control-operation-log/${invoice_id}`,
          method: 'GET',
        }
      }
    }),
    getCustomerBalance: builder.query({
      query: (data) => {
        let params = {
          customer: data.customer,
          username: data.username,
          business_type: data.business_type,
          contact: data.contact,
          page_size: data.pageSize,
          curr_page: data.current,
        }
        return {
          url: 'advance_payment/get-customer-balance/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['CustomerBalance']
    }),
    getCredits: builder.query({
      query: (data) => {
        let params = {
          customer: data.customer,
          username: data.username,
          business_type: data.business_type,
          credit_type: data.credit_type,
          issuer: data.issuer,
          system: data.system,
          page_size: data.pageSize,
          curr_page: data.current,
        }
        if (data?.issue_time) {
          params.issue_time_after = data.issue_time[0].substring(0,10);
          params.issue_time_before = data.issue_time[1].substring(0,10);
        }
        return {
          url: 'advance_payment/get-credits/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['Credits']
    }),
    createCredit: builder.mutation({
      query: (data) => {
        return {
          url: 'advance_payment/create-new-credit/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['Credits', 'CustomerBalance']
    }),
    getBankSlip: builder.query({
      query: (data) => {
        let params = {
          slip_status: data.slip_status,
          name: data.name,
          bank_slip_type: data.bank_slip_type,
          remittee: data.remittee,
          collection_amount: data.collection_amount,
          payee_name: data.payee_name,
          issuer: data.issuer,
          note: data.note,
          page_size: data.pageSize,
          curr_page: data.current,
        }
        if (data?.issue_time) {
          params.issue_time_after = data.issue_time[0].substring(0,10);
          params.issue_time_before = data.issue_time[1].substring(0,10);
        }
        if (data?.confirm_date) {
          params.confirm_date_after = data.confirm_date[0].substring(0,10);
          params.confirm_date_before = data.confirm_date[1].substring(0,10);
        }
        return {
          url: 'advance_payment/get-bank-slip/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['BankSlip']
    }),
    createBankSlip: builder.mutation({
      query: (data) => {
        return {
          url: 'advance_payment/create-new-bank-slip/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['BankSlip', 'AdvancePayment', 'CustomerBalance']
    }),
    bankSlipConfirmPayment: builder.mutation({
      query: (data) => {
        return {
          url: 'advance_payment/bank-slip-confirm-payment/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['BankSlip', 'CustomerBalance', 'AdvancePayment', 'CustomerBalance']
    }),
    getAdvancePayment: builder.query({
      query: (data) => {
        let params = {
          slip_name: data.slip_name,
          uid: data.uid,
          issuer: data.issuer,
          page_size: data.pageSize,
          curr_page: data.current,
        }
        if (data?.issue_time) {
          params.issue_time_after = data.issue_time[0].substring(0,10);
          params.issue_time_before = data.issue_time[1].substring(0,10);
        }
        return {
          url: 'advance_payment/get-advance-payment/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['AdvancePayment']
    }),
    getCustomerUidWithBillingCycle: builder.query({
      query: (data) => {
        return {
          url: 'accounts/get-customer-uid-with-business-type/?billing_cycle=' + data,
          method: 'GET',
        }
      },
      providesTags: ['CustomerUidWithBillingCycleSearchSelect']
    }),
    getCustomerGroup: builder.query({
      query: () => {
        return {
          url: 'accounts/get-customer-group/',
          method: 'GET',
        }
      },
      providesTags: ['CustomerGroup']
    }),
    getLocationByName: builder.query({
      query: () => {
        return {
          url: 'accounts/get-location-by-name/',
          method: 'GET',
        }
      }
    }),
    getSinglePieceInvoiceTemplates: builder.query({
      query: () => {
        return {
          url: 'accounts/get-single-piece-invoice-templates/',
          method: 'GET',
        }
      },
      providesTags: ['SinglePieceInvoiceTemplates']
    }),
    getMultiPieceInvoiceTemplates: builder.query({
      query: () => {
        return {
          url: 'accounts/get-multi-piece-invoice-templates/',
          method: 'GET',
        }
      },
      providesTags: ['MultiPieceInvoiceTemplates'],
    }),
    getRoleByName: builder.query({
      query: () => {
        return {
          url: 'accounts/get-role-by-name/',
          method: 'GET',
        }
      }
    }),
    getRoleByDepartment: builder.query({
      query: () => {
        return {
          url: 'accounts/get-role-by-department/',
          method: 'GET',
        }
      }
    }),
    shipmentConsolidation: builder.mutation({
      query: (data) => {
        return {
          url: 'accounts/shipment-consolidation/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['GroundCalculationDetails'],
    }),
    getGroundServices: builder.query({
      query: () => {
        return {
          url: 'accounts/get-ground-services/',
          method: 'GET',
        }
      }
    }),
    getPayableInvoices: builder.query({
      query: (data) => {
        let params = {
          status: data.status,
          edi_master_number: data.edi_master_number,
          invoice_number: data.invoice_number,
          account_number: data.account_number,
          payment_method_indicator: data.payment_method_indicator,
          page_size: data.pageSize,
          curr_page: data.current,
        }
        if (data?.eft_settlement_date) {
          params.eft_settlement_date = data.eft_settlement_date.substring(0,10);
        }
        if (data?.invoice_date) {
          params.invoice_date_after = data.invoice_date[0].substring(0,10);
          params.invoice_date_before = data.invoice_date[1].substring(0,10);
        }
        return {
          url: 'accounts/get-payable-invoices/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['PayableInvoices']
    }),
    holdPayableInvoice: builder.mutation({
      query: (data) => {
        return {
          url: 'accounts/hold-payable-invoice/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['PayableInvoices']
    }),
    releasePayableInvoice: builder.mutation({
      query: (data) => {
        return {
          url: 'accounts/release-payable-invoice/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['PayableInvoices']
    }),
    payableInvoicePaymentSelect: builder.mutation({
      query: (data) => {
        return {
          url: 'accounts/payable-invoice-payment-through-selection/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['PayableInvoices', 'AsyncManagement']
    }),
    downloadPayableInvoices: builder.mutation({
      query: (data) => {
        let params = {
          status: data.status,
          edi_master_number: data.edi_master_number,
          invoice_number: data.invoice_number,
          account_number: data.account_number,
          payment_method_indicator: data.payment_method_indicator,
          page_size: data.pageSize,
          curr_page: data.current,
        }
        if (data?.eft_settlement_date) {
          params.eft_settlement_date = data.eft_settlement_date.format('YYYY-MM-DD');
        }
        if (data?.invoice_date) {
          params.invoice_date_after = data.invoice_date[0].format('YYYY-MM-DD');
          params.invoice_date_before = data.invoice_date[1].format('YYYY-MM-DD');
        }
        return {
          url: 'accounts/get-payable-invoices/',
          method: 'POST',
          params: params,
        }
      },
      invalidatesTags: ['AsyncManagement']
    }),
    payableInvoicePaymentSearch: builder.mutation({
      query: (data) => {
        let params = {
          status: data.status,
          edi_master_number: data.edi_master_number,
          invoice_number: data.invoice_number,
          account_number: data.account_number,
          payment_method_indicator: data.payment_method_indicator,
          page_size: data.pageSize,
          curr_page: data.current,
        }
        if (data?.eft_settlement_date) {
          params.eft_settlement_date = data.eft_settlement_date.format('YYYY-MM-DD');
        }
        if (data?.invoice_date) {
          params.invoice_date_after = data.invoice_date[0].format('YYYY-MM-DD');
          params.invoice_date_before = data.invoice_date[1].format('YYYY-MM-DD');
        }
        return {
          url: 'accounts/payable-invoice-payment-through-search/',
          method: 'POST',
          params: params,
          body: data.formData,
          formData: true,
        }
      },
      invalidatesTags: ['PayableInvoices', 'AsyncManagement']
    }),
    getCostRateExcel: builder.query({
      query: (searchData) => {
        let params = {
          logistics_supplier: searchData.logisticsSupplier,
          effective_date: searchData.effectiveDate?.substring(0,10),
          page_size: searchData.pageSize,
          curr_page: searchData.current,
        }
        return {
          url: 'accounts/get-cost-rate-excel/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['CostRateExcel'],
    }),
    verifyCostQuoteSheet: builder.mutation({
      query: (data) => ({
        url: 'accounts/verify-cost-quote-sheet/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['CostRateExcel'],
    }),
    getCostException: builder.query({
      query: (searchData) => {
        let params = {
          tracking_id: searchData.tracking_id,
          uid: searchData.uid,
          billing_invoice_name: searchData.billing_invoice_name,
          logistics_service: searchData.logistics_service,
          exception_description: searchData.exception_description,
          page_size: searchData.pageSize,
          curr_page: searchData.current,
        }
        return {
          url: 'accounts/get-cost-exception/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['CostException'],
    }),
    getCostExceptionWithError: builder.query({
      query: (searchData) => {
        let params = {
          tracking_id: searchData.tracking_id,
          uid: searchData.uid,
          billing_invoice_name: searchData.billing_invoice_name,
          page_size: searchData.pageSize,
          curr_page: searchData.current,
        }

        return {
          url: 'accounts/get-cost-exception-with-error/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['CostExceptionWithError'],
    }),
    costExceptionWithErrorBulkOperationBySelect: builder.mutation({
      query: (data) => {
        return {
          url: 'accounts/cost-exception-with-error-bulk-operation-by-select/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['AsyncManagement', 'CostExceptionWithError'],
    }),
    costExceptionWithErrorBulkOperationBySearch: builder.mutation({
      query: (searchData) => {
        let params = {
          tracking_id: searchData.tracking_id,
          uid: searchData.uid,
          billing_invoice_name: searchData.billing_invoice_name,
        }
        const formData = new FormData();
        formData.append('operation', searchData.operation);
        return {
          url: 'accounts/get-cost-exception-with-error/',
          method: 'POST',
          params: params,
          body: formData,
          formData: true,
        }
      },
      invalidatesTags: ['AsyncManagement', 'CostExceptionWithError'],
    }),
    costExceptionBulkOperationBySelect: builder.mutation({
      query: (data) => {
        return {
          url: 'accounts/cost-exception-bulk-operation-by-select/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['AsyncManagement', 'CostException', 'CostExceptionDispute'],
    }),
    costExceptionBulkOperationBySearch: builder.mutation({
      query: (searchData) => {
        let params = {
          tracking_id: searchData.tracking_id,
          uid: searchData.uid,
          billing_invoice_name: searchData.billing_invoice_name,
          logistics_service: searchData.logistics_service,
        }
        const formData = new FormData();
        formData.append('operation', searchData.operation);
        return {
          url: 'accounts/get-cost-exception/',
          method: 'POST',
          params: params,
          body: formData,
          formData: true,
        }
      },
      invalidatesTags: ['AsyncManagement', 'CostException', 'CostExceptionDispute'],
    }),
    getCostExceptionDispute: builder.query({
      query: (searchData) => {
        let params = {
          tracking_id: searchData.tracking_id,
          uid: searchData.uid,
          billing_invoice_name: searchData.billing_invoice_name,
          logistics_service: searchData.logistics_service,
          page_size: searchData.pageSize,
          curr_page: searchData.current,
        }
        if (searchData?.dispute_date) {
          params.dispute_date = searchData.dispute_date.substring(0,10);
        }
        return {
          url: 'accounts/get-cost-exception-dispute/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['CostExceptionDispute'],
    }),
    costExceptionDisputeBulkOperationBySelect: builder.mutation({
      query: (data) => {
        return {
          url: 'accounts/cost-exception-dispute-bulk-operation-by-select/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['AsyncManagement', 'CostExceptionDispute'],
    }),
    AddCustomerSurcharge: builder.mutation({
      query: (data) => ({
        url: 'accounts/customer-add-surcharges/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['QuoSheet'],
    }),
    costExceptionDisputeBulkOperationBySearch: builder.mutation({
      query: (searchData) => {
        let params = {
          tracking_id: searchData.tracking_id,
          uid: searchData.uid,
          billing_invoice_name: searchData.billing_invoice_name,
          logistics_service: searchData.logistics_service,
          page_size: searchData.pageSize,
          curr_page: searchData.current,
        }

        if (searchData?.dispute_date) {
          params.dispute_date = searchData.dispute_date.format('YYYY-MM-DD');
        }

        const formData = new FormData();
        formData.append('operation', searchData.operation);

        return {
          url: 'accounts/get-cost-exception-dispute/',
          method: 'POST',
          body: formData,
          formData: true,
        }
      },
      invalidatesTags: ['AsyncManagement', 'CostExceptionDispute'],
    }),
    getSurchargeInfo: builder.query({
      query: (data) => {
        return {
          url: 'accounts/surcharge-info/',
          method: 'GET',
          params: data,
        }
      }
    }),
    // 新增的 POST 接口
    createOrUpdateSurchargeInfo: builder.mutation({
      query: (data) => ({
        url: 'accounts/surcharge-info/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ThirdPartyBillingDetailsWithError'],
    }),
    getSurcharge: builder.query({
      query: (data) => {
        return {
          url: 'accounts/get-surcharge/',
          method: 'GET',
          params: data,
        }
      }
    }),
    // TODO 外采渠道往下
    getThirdPartyBillingDetails: builder.query({
      query: (searchData) => {
        let params = {
          status: searchData.status,
          billing_invoice: searchData.billing_invoice,
          tracking_number: searchData.tracking_number,
          service_type: searchData.service_type,
          master_tracking_number: searchData.master_tracking_number,
          uid: searchData.uid,
          requires_dispute: searchData.requires_dispute,
          page_size: searchData.pageSize,
          curr_page: searchData.current,
        }
        if (searchData?.shipment_date) {
          params.shipment_date_after = searchData.shipment_date[0].substring(0,10);
          params.shipment_date_before = searchData.shipment_date[1].substring(0,10);
        }
        return {
          url: 'third_party/get-third-party-billing-details/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['ThirdPartyBillingDetails'],
    }),
    getThirdPartyBillingInvoiceNames: builder.query({
      query: (data) => ({
        url: 'third_party/get-third-party-billing-invoices-names/',
        method: 'GET',
      }),
      providesTags: ['ThirdPartyBillingInvoiceSearchSelect']
    }),
    getThirdPartyBillingDetailsWithError: builder.query({
      query: (searchData) => {
        return Object.keys(searchData).length > 0
            ? {
              url: 'third_party/get-third-party-billing-details-with-error/',
              method: 'GET',
              params: {
                billing_invoice: searchData.billingInvoice,
                tracking_number: searchData.trackingNumber,
                uid: searchData.uid,
                status: searchData.currentTab,
                dup_status: searchData.dup_status,
                service_match: searchData.service_match,
                logistics_service: searchData.logisticsService,
                master_mismatch_status: searchData.masterMismatchStatus,
                user_defined_exceptions: searchData.userDefinedExceptions,
                zzz_price: searchData.zzzPrice,
                page_size: searchData.pageSize,
                curr_page: searchData.currentPage,
              },
            }
            : {
              url: 'third_party/get-third-party-billing-details-with-error/',
              method: 'GET',
              params: {
                status: searchData.currentTab,
                page_size: searchData.pageSize,
                curr_page: searchData.currentPage,
              },
            };
      },
      providesTags: ['ThirdPartyBillingDetailsWithError'],
    }),
    getThirdPartyCalculationInfo: builder.query({
      query: (searchData) => {
        let params = {
          status: searchData.status,
          billing_invoice: searchData.billing_invoice,
          tracking_number: searchData.tracking_number,
          logistics_service: searchData.logistics_service,
          master_tracking_number: searchData.master_tracking_number,
          uid: searchData.uid,
          invoice: searchData.invoice,
          page_size: searchData.pageSize,
          curr_page: searchData.current,
        }
        if (searchData?.shipment_date) {
          params.shipment_date_after = searchData.shipment_date[0].substring(0,10);
          params.shipment_date_before = searchData.shipment_date[1].substring(0,10);
        }
        return {
          url: 'third_party/get-third-party-calculation-info/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['ThirdPartyCalculationInfo'],
    }),
    getChannelPartner: builder.query({
      query: () => ({
        url: 'third_party/get-all-channel-partner/',
        method: 'GET',
      }),
      providesTags: ['LogisticsSupplier'],
    }),
    getThirdPartyDupDetails: builder.query({
      query: (data) => {
        return {
          url: 'third_party/get-third-party-dup-details/',
          method: 'GET',
          params: data,
        }
    }
    }),
    downloadThirdPartyBillingDetailsBySelect: builder.mutation({
      query: (data) => {
        return {
          url: 'third_party/download-third-party-billing-details-by-select/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['AsyncManagement'],
    }),
    downloadThirdPartyBillingDetailsBySearch: builder.mutation({
      query: (searchData) => {
        let params = {
          status: searchData.status,
          billing_invoice: searchData.billing_invoice,
          tracking_number: searchData.tracking_number,
          logistics_service: searchData.logistics_service,
          master_tracking_number: searchData.master_tracking_number,
          uid: searchData.uid,
          invoice: searchData.invoice,
        }
        if (searchData?.shipment_date) {
          params.shipment_date_after = searchData.shipment_date[0].substring(0,10);
          params.shipment_date_before = searchData.shipment_date[1].substring(0,10);
        }
        const formData = new FormData();
        formData.append('operation', searchData.operation);
        return {
          url: 'third_party/get-third-party-billing-details/',
          method: 'POST',
          params: params,
          body: formData,
          formData: true,
        }
      },
      invalidatesTags: ['AsyncManagement', 'ThirdPartyBillingDetails'],
    }),
    thirdPartyGenerateBillingBatchBySelect: builder.mutation({
      query: (data) => {
        return {
          url: 'third_party/third-party-generate-billing-batch-by-select/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['AsyncManagement', 'ThirdPartyCalculationInfo', 'ThirdPartyBillingDetails', 'ThirdPartyBillingBatch'],
    }),
    thirdPartyUpdateUid: builder.mutation({
      query: (data) => ({
        url: 'third_party/third-party-update-uid-with-billing-details/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['BillingInvoice', 'ThirdPartyBillingDetailsWithError', 'ThirdPartyBillingDetails', 'BillingProcess', 'BillingInvoice'],
    }),
    thirdPartyStatusUpdateHandler: builder.mutation({
      query: (data) => ({
        url: 'third_party/third-party-billing-details-status-update/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ThirdPartyBillingDetails', 'ThirdPartyBillingDetailsWithError', 'BillingProcess'],
    }),
    getThirdPartyBillingBatch: builder.query({
      query: (searchData) => {
        let params = {
          name: searchData.name,
          customer: searchData.customer,
          billing_invoice_names: searchData.billing_invoice_names,
          logistics_service_names: searchData.logistics_service_names,
          status: searchData.status,
          user: searchData.user,
          page_size: searchData.pageSize,
          curr_page: searchData.current,
        }
        if (searchData?.bill_time) {
          params.bill_time_after = searchData.bill_time[0].substring(0,10);
          params.bill_time_before = searchData.bill_time[1].substring(0,10);
        }
        return {
          url: 'third_party/get-third-party-billing-batch/',
          method: 'GET',
          params: params,
        }
      },
      providesTags: ['ThirdPartyBillingBatch'],
    }),
    thirdPartyDetailCalculationBySelect: builder.mutation({
      query: (data) => {
        return {
          url: 'third_party/third-party-detail-calculation-by-select/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['AsyncManagement', 'ThirdPartyBillingDetails', 'ThirdPartyCalculationInfo'],
    }),
    thirdPartyDetailCalculationBulkOperationBySearch: builder.mutation({
      query: (searchData) => {
        let params = {
          status: searchData.status,
          billing_invoice: searchData.billing_invoice,
          tracking_number: searchData.tracking_number,
          logistics_service: searchData.logistics_service,
          master_tracking_number: searchData.master_tracking_number,
          uid: searchData.uid,
          invoice: searchData.invoice,
        }
        if (searchData?.shipment_date) {
          params.shipment_date_after = searchData.shipment_date[0].format('YYYY-MM-DD');
          params.shipment_date_before = searchData.shipment_date[1].format('YYYY-MM-DD');
        }
        const formData = new FormData();
        formData.append('operation', searchData.operation);
        if (searchData?.billing_batch_name) {
          formData.append('billing_batch_name', searchData.billing_batch_name);
        }
        return {
          url: 'third_party/get-third-party-calculation-info/',
          method: 'POST',
          params: params,
          body: formData,
          formData: true,
        }
      },
      invalidatesTags: ['AsyncManagement', 'ThirdPartyBillingDetails', 'ThirdPartyCalculationInfo', 'ThirdPartyBillingBatch'],
    }),
    downloadThirdPartyBillingBatchBySelect: builder.mutation({
      query: (data) => {
        return {
          url: 'third_party/download-third-party-billing-batch-by-select',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['AsyncManagement', 'ThirdPartyBillingBatch'],
    }),
    thirdPartyDisputeBillingDetails: builder.mutation({
      query: (data) => ({
        url: 'third_party/third-party-dispute-billing-details/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ThirdPartyBillingDetails', 'ThirdPartyBillingDetailsWithError', 'BillingProcess'],
    }),
    thirdPartyDisputeBillingDetailsV2: builder.mutation({
      query: (data) => ({
        url: 'third_party/third-party-dispute-billing-details-v2/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ThirdPartyBillingDetails', 'ThirdPartyBillingDetailsWithError', 'BillingProcess'],
    }),
    thirdPartyDeleteBillingInvoice: builder.mutation({
      query: (data) => ({
        url: `accounts/delete-billing-invoice/`,
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: ['BillingInvoice', 'BillingProcess', 'ThirdPartyBillingDetails', 'BillingInvoiceSearchSelect'],
    }),
    downloadCustomerBalanceActivities: builder.mutation({
      query: (data) => {
        return {
          url: 'advance_payment/download-customer-balance-activities/',
          method: 'POST',
          body: data,
          formData: true,
        }
      },
      invalidatesTags: ['AsyncManagement'],
    }),
    ThirdPartyDoubleCheckBillingDetailsWithError: builder.mutation({
      query: (data) => ({
        url: 'third_party/third-party-double-check-billing-details-with-error/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ThirdPartyBillingDetailsWithError', 'ThirdPartyBillingDetails'],
    }),
    GetCustomerName: builder.mutation({
      query: (data) => ({
        url: `accounts/get-customer-name/`,
        method: 'GET',
        params: data,
      }),
      invalidatesTags: ['PendingClient'],
    }),
    downloadCreditData: builder.mutation({
      query: (data) => {
        let params = {
          customer: data.customer,
          username: data.username,
          business_type: data.business_type,
          credit_type: data.credit_type,
          issuer: data.issuer,
          system: data.system,
        }
        if (data?.issue_time) {
          params.issue_time_after = data.issue_time[0].format('YYYY-MM-DD');
          params.issue_time_before = data.issue_time[1].format('YYYY-MM-DD');
        }
        return {
          url: 'advance_payment/get-credits/',
          method: 'POST',
          params: params,
        }
      },
      invalidatesTags: ['AsyncManagement'],
    }),
    downloadBankSlipData: builder.mutation({
      query: (data) => {
        let params = {
          slip_status: data.slip_status,
          name: data.name,
          bank_slip_type: data.bank_slip_type,
          remittee: data.remittee,
          collection_amount: data.collection_amount,
          payee_name: data.payee_name,
          issuer: data.issuer,
          note: data.note,
        }
        if (data?.issue_time) {
          params.issue_time_after = data.issue_time[0].format('YYYY-MM-DD');
          params.issue_time_before = data.issue_time[1].format('YYYY-MM-DD');
        }
        if (data?.confirm_date) {
          params.confirm_date_after = data.confirm_date[0].format('YYYY-MM-DD');
          params.confirm_date_before = data.confirm_date[1].format('YYYY-MM-DD');
        }
        return {
          url: 'advance_payment/get-bank-slip/',
          method: 'POST',
          params: params,
        }
      },
      providesTags: ['AsyncManagement']
    }),
    downloadAdvancePaymentData: builder.mutation({
      query: (data) => {
        let params = {
          slip_name: data.slip_name,
          uid: data.uid,
          issuer: data.issuer,
        }
        if (data?.issue_time) {
          params.issue_time_after = data.issue_time[0].format('YYYY-MM-DD');
          params.issue_time_before = data.issue_time[1].format('YYYY-MM-DD');
        }
        return {
          url: 'advance_payment/get-advance-payment/',
          method: 'POST',
          params: params,
        }
      },
      providesTags: ['AsyncManagement']
    }),
    withdrawInvoice: builder.mutation({
      query: (data) => ({
        url: 'accounts/withdraw-invoice/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['Invoice'],
    }),
    editAccountInvoiceBillingCycle: builder.mutation({
      query: (data) => ({
        url: 'accounts/edit-account-invoice-billing-cycle/',
        method: 'POST',
        body: data,
        formData: true,
      }),
      invalidatesTags: ['AccountReceivable'],
    })
  }),
})

export const {
  useGetUserQuery,
  useGetClientQuery,
  useUpdateClientMutation,
  useBulkUpdateClientStatusMutation,
  useUpdateClientMWTMutation,
  useCreateClientMutation,
  useCreateUserManagementMutation,
  useCreateRoleMutation,
  useDeleteClientMutation,
  useGetRateTemplateQuery,
  useGetLogisticsSupplierQuery,
  useGetChannelPartnerQuery,
  useGetLogisticsServiceQuery,
  useCreateLogisticsServiceMutation,
  useUpdateLogisticsServiceMutation,
  useGetQuoteSheetMutation,
  useGetRateExcelQuery,
  useGetAllCustomerUidQuery,
  useGetLogisticsServiceByNameQuery,
  useVerifyQuoteSheetMutation,
  useGetBillingInvoiceQuery,
  useUploadBillingInvoiceMutation,
  useDeleteBillingInvoiceMutation,
  useGetBillingProcessQuery,
  useGetBillingInvoiceNamesQuery,
  useGetBillingDetailsQuery,
  useGetBillingDetailsWithErrorQuery,
  useGetThirdPartyBillingDetailsWithErrorQuery,
  useUpdateUidMutation,
  useBatchUpdateUidMutation,
  useGetAsyncManagementQuery,
  useDeleteAsyncManagementMutation,
  useExportUidFilesMutation,
  useGetRecordTypeQuery,
  useDisputeBillingDetailsMutation,
  useStatusUpdateHandlerMutation,
  useStatusUpdateHandlerV2Mutation,
  useGenerateBillingBatchDetailsMutation,
  useSunkCostMutation,
  useGetFuelSurchargeManagementQuery,
  useUpdateFuelSurchargeManagementMutation,
  useGetNonStandardServiceQuery,
  useGetInvoiceQuery,
  useDownloadBillingBatchMutation,
  useUpdateMasterTrackingMutation,
  useGetTrackingMasterTrackingMutation,
  useModifyServiceMutation,
  useGetNonOriginalBillingDetailsQuery,
  useGetAWSFileManagementQuery,
  useGetChargeCodeQuery,
  useGetChargeCodeDescriptionsQuery,
  useConvertZZZToChargeMutation,
  useUpdateUidWithoutStatusChangeMutation,
  useSplitUserChangeMutation,
  useShipmentRateCalculationMutation,
  useReShipmentRateCalculationMutation,
  useGetGroundCalculationDetailsQuery,
  useGetHSPCalculationDetailsQuery,
  useGetDupDetailsQuery,
  useCompulsoryPaymentMutation,
  useTemporaryRetrieveMasterTrackingMutation,
  useGroundShipmentRateCalculationMutation,
  useGenerateBillingBatchShipmentsMutation,
  useCreateRateTemplateMutation,
  useUpdateClientDateIntervalMutation,
  useAddNewFuelSurchargeMutation,
  useGetRuleExpressionsQuery,
  useCreateRuleExpressionMutation,
  useAddCostSurchargeMutation,
  useEditRuleExpressionMutation,
  useDeleteRuleExpressionMutation,
  useGetRuleExpressionsNamesQuery,
  useReevaluateBillingDetailsMutation,
  useGetAllCustomerWithGroupQuery,
  useUpdateBillingStatusMutation,
  useLabelReuseMutation,
  useGetBillingDetailsSummaryQuery,
  useGetInvoiceByNameQuery,
  useGetRuleExpressionNamesQuery,
  useGetUserByDepartmentQuery,
  useEditBillingBatchInfoMutation,
  useGetAccountReceivableQuery,
  useCreateAccountReceivableMutation,
  useEditingAccountReceivableMutation,
  useMakePaymentMutation,
  useUpdateAccountReceivableMutation,
  useAdjustSurchargesMutation,
  useDisputeSurchargesMutation,
  useGetAccountReceivableAndRiskControlOperationLogQuery,
  useGetCustomerBalanceQuery,
  useGetCreditsQuery,
  useCreateCreditMutation,
  useGetBankSlipQuery,
  useCreateBankSlipMutation,
  useBankSlipConfirmPaymentMutation,
  useGetAdvancePaymentQuery,
  useGetCustomerUidWithBillingCycleQuery,
  useGetUserManagementQuery,
  useGetRolesQuery,
  useGetDepartmentsQuery,
  useUpdateUserManagementMutation,
  useUpdateRoleMutation,
  useUpdateDepartmentMutation,
  useCreateDepartmentMutation,
  useCreateLocationMutation,
  useGetLocationQuery,
  useDeleteAccountReceivableMutation,
  useUpdateLocationMutation,
  useGetCustomerGroupQuery,
  useGetLocationByNameQuery,
  useCreatePendingClientMutation,
  useGetPendingCustomerQuery,
  useRejectTheReviewMutation,
  useGetSinglePieceInvoiceTemplatesQuery,
  useGetMultiPieceInvoiceTemplatesQuery,
  useGetRoleByNameQuery,
  useGetRoleByDepartmentQuery,
  useShipmentConsolidationMutation,
  useGetGroundServicesQuery,
  useGetPayableInvoicesQuery,
  useHoldPayableInvoiceMutation,
  useReleasePayableInvoiceMutation,
  usePayableInvoicePaymentSelectMutation,
  useDownloadPayableInvoicesMutation,
  usePayableInvoicePaymentSearchMutation,
  useGetCostRateExcelQuery,
  useVerifyCostQuoteSheetMutation,
  useGetCostExceptionQuery,
  useGetCostExceptionWithErrorQuery,
  useCostExceptionWithErrorBulkOperationBySelectMutation,
  useCostExceptionWithErrorBulkOperationBySearchMutation,
  useCostExceptionBulkOperationBySelectMutation,
  useCostExceptionBulkOperationBySearchMutation,
  useGetCostExceptionDisputeQuery,
  useCostExceptionDisputeBulkOperationBySelectMutation,
  useCostExceptionDisputeBulkOperationBySearchMutation,
  useGetSurchargeInfoQuery,
  useGetSurchargeQuery,
  useCreateOrUpdateSurchargeInfoMutation,
  useGetThirdPartyBillingDetailsQuery,
  useGetThirdPartyBillingInvoiceNamesQuery,
  useGetThirdPartyCalculationInfoQuery,
  useGetThirdPartyDupDetailsQuery,
  useGetCustomerNameMutation,
  useDownloadThirdPartyBillingDetailsBySelectMutation,
  useDownloadThirdPartyBillingDetailsBySearchMutation,
  useThirdPartyGenerateBillingBatchBySelectMutation,
  useThirdPartyUpdateUidMutation,
  useGetThirdPartyBillingBatchQuery,
  useThirdPartyDetailCalculationBySelectMutation,
  useThirdPartyDetailCalculationBulkOperationBySearchMutation,
  useDownloadThirdPartyBillingBatchBySelectMutation,
  useAddCustomerSurchargeMutation,
  useThirdPartyStatusUpdateHandlerMutation,
  useThirdPartyDisputeBillingDetailsMutation,
  useThirdPartyDeleteBillingInvoiceMutation,
  useDownloadCustomerBalanceActivitiesMutation,
  useThirdPartyDisputeBillingDetailsV2Mutation,
  useThirdPartyDoubleCheckBillingDetailsWithErrorMutation,
  useDownloadCreditDataMutation,
  useDownloadBankSlipDataMutation,
  useDownloadAdvancePaymentDataMutation,
  useWithdrawInvoiceMutation,
  useEditAccountInvoiceBillingCycleMutation,
} = apiSlice